import { useCallback, useEffect, useState } from "react";
import { Save } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import { Card } from "@/components/ui/Card";
import { FormMessage, type Feedback } from "@/components/ui/FormMessage";
import { EMPTY_OIDC_CONFIG, type GenericResponse, type OidcConfig } from "@/lib/types";

export function SsoPage() {
  const [config, setConfig] = useState<OidcConfig>(EMPTY_OIDC_CONFIG);
  const [hasSecret, setHasSecret] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<OidcConfig>("/api/settings/oidc");
      setHasSecret(Boolean(data.has_client_secret));
      // client_secret kommt maskiert (leer) — leer lassen behält das alte.
      setConfig({ ...EMPTY_OIDC_CONFIG, ...data, client_secret: "" });
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Laden fehlgeschlagen") });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const patch = (p: Partial<OidcConfig>) => setConfig((c) => ({ ...c, ...p }));

  const onSave = async () => {
    setSaving(true);
    setFeedback(null);
    const body: OidcConfig = {
      ...config,
      issuer_url: config.issuer_url.trim(),
      client_id: config.client_id.trim(),
      admin_groups: config.admin_groups.map((g) => g.trim()).filter(Boolean),
    };
    try {
      const resp = await api.post<GenericResponse>("/api/settings/oidc", body);
      if (resp.success) {
        setFeedback({ kind: "success", text: resp.message });
        await reload();
      } else {
        setFeedback({ kind: "error", text: resp.message });
      }
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Speichern fehlgeschlagen") });
    } finally {
      setSaving(false);
    }
  };

  const callbackHint = `${window.location.origin}/api/auth/oidc/callback`;

  return (
    <div className="flex flex-col gap-4 p-6">
      <FormMessage feedback={feedback} />

      <Card
        title={t("Single Sign-On (OIDC)")}
        description={t("Anmeldung über Microsoft Entra ID, Keycloak, Authentik u.a. Ein Provider pro Instanz.")}
      >
        {loading ? (
          <div className="text-sm text-muted-foreground">{t("Lade…")}</div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="flex cursor-pointer items-center gap-2 md:col-span-2">
              <input
                type="checkbox"
                checked={config.enabled}
                onChange={(e) => patch({ enabled: e.target.checked })}
                className="h-3.5 w-3.5 cursor-pointer accent-primary"
              />
              <span className="text-sm font-medium">{t("SSO aktiviert")}</span>
            </label>

            <Field
              label={t("Issuer-URL")}
              value={config.issuer_url}
              onChange={(v) => patch({ issuer_url: v })}
              placeholder="https://login.microsoftonline.com/<tenant>/v2.0"
              className="md:col-span-2"
              mono
            />

            <Field
              label={t("Client-ID")}
              value={config.client_id}
              onChange={(v) => patch({ client_id: v })}
              placeholder={t("App-Registration Client-ID")}
              mono
            />

            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t("Client-Secret")}
              </span>
              <input
                type="password"
                value={config.client_secret}
                onChange={(e) => patch({ client_secret: e.target.value })}
                placeholder={hasSecret ? t("•••••••• (gesetzt – leer lassen zum Behalten)") : t("Client-Secret")}
                autoComplete="off"
                className="h-9 rounded-md border border-border bg-background px-3 font-mono text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
            </label>

            <Field
              label={t("Scopes")}
              value={config.scopes}
              onChange={(v) => patch({ scopes: v })}
              placeholder="openid email profile groups"
              mono
            />

            <Field
              label={t("Gruppen-Claim")}
              value={config.groups_claim}
              onChange={(v) => patch({ groups_claim: v })}
              placeholder="groups"
              mono
            />

            <label className="flex flex-col gap-1 md:col-span-2">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t("Admin-Gruppen (eine pro Zeile)")}
              </span>
              <textarea
                value={config.admin_groups.join("\n")}
                onChange={(e) => patch({ admin_groups: e.target.value.split("\n") })}
                rows={3}
                placeholder={"leif-admins\nplatform-team"}
                className="resize-y rounded-md border border-border bg-background p-2 font-mono text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
              <span className="text-[11px] text-muted-foreground">
                {t(
                  "Mitglieder dieser IdP-Gruppen werden bei jedem Login zu Admins; alle anderen bekommen die Standardrolle. Leer = niemand wird per Gruppe Admin.",
                )}
              </span>
            </label>

            <div className="flex flex-col gap-1">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t("Standardrolle für neue Nutzer")}
              </span>
              <select
                value={config.default_role}
                onChange={(e) => patch({ default_role: e.target.value as "admin" | "viewer" })}
                className="h-9 rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              >
                <option value="viewer">{t("Viewer (nur lesen)")}</option>
                <option value="admin">{t("Admin")}</option>
              </select>
            </div>

            <label className="flex items-center gap-2 pt-6">
              <input
                type="checkbox"
                checked={config.auto_provision}
                onChange={(e) => patch({ auto_provision: e.target.checked })}
                className="h-3.5 w-3.5 cursor-pointer accent-primary"
              />
              <span className="text-sm">{t("Unbekannte Nutzer automatisch anlegen")}</span>
            </label>

            <Field
              label={t("Öffentliche Basis-URL (optional)")}
              value={config.redirect_base_url}
              onChange={(v) => patch({ redirect_base_url: v })}
              placeholder="https://leif.example.com"
              className="md:col-span-2"
              mono
            />

            <Field
              label={t("Button-Beschriftung")}
              value={config.button_label}
              onChange={(v) => patch({ button_label: v })}
              placeholder={t("Mit Microsoft anmelden")}
              className="md:col-span-2"
            />

            <div className="rounded-md border border-border bg-muted/40 p-3 text-[11px] text-muted-foreground md:col-span-2">
              {t("Redirect-URI beim IdP registrieren:")}{" "}
              <code className="font-mono text-foreground">{callbackHint}</code>
              {config.redirect_base_url.trim()
                ? " " + t("(bzw. mit der oben gesetzten Basis-URL)")
                : " " + t("— hinter einem Reverse-Proxy die öffentliche Basis-URL oben setzen.")}
            </div>

            <div className="md:col-span-2">
              <button
                type="button"
                onClick={onSave}
                disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-60"
              >
                <Save className="h-3.5 w-3.5" /> {saving ? t("Speichern…") : t("Speichern")}
              </button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  className,
  mono,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
  mono?: boolean;
}) {
  return (
    <label className={"flex flex-col gap-1 " + (className ?? "")}>
      <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        className={
          "h-9 rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary " +
          (mono ? "font-mono text-xs" : "")
        }
      />
    </label>
  );
}
